import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { buildClassName } from 'utils/build-class-name';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from './ui/table';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { generateAmortizationSchedule } from 'utils/mortgage-calculator/mortgageCalculations';
import cardStyles from './ui/card.module.scss';
import styles from './AmortizationChart.module.scss';

interface AmortizationChartProps {
  principal: number;
  interestRate: number;
  amortization: number;
}

const AmortizationChart = ({ principal, interestRate, amortization }: AmortizationChartProps) => {
  const [selectedYears, setSelectedYears] = useState(amortization);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'yearly' | 'monthly'>('yearly');

  const schedule = generateAmortizationSchedule(principal, interestRate, selectedYears);

  // Calculate totals for display
  const totalPrincipal = schedule.reduce((sum, item) => sum + item.principal, 0);
  const totalInterest = schedule.reduce((sum, item) => sum + item.interest, 0);
  const totalPayments = totalPrincipal + totalInterest;

  // Generate monthly schedule
  const generateMonthlySchedule = () => {
    const monthlyRate = interestRate / 100 / 12;
    const totalMonths = selectedYears * 12;
    const monthlyPayment = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / (Math.pow(1 + monthlyRate, totalMonths) - 1);

    const monthlyData: { month: number; principal: number; interest: number; balance: number }[] = [];
    let balance = principal;

    for (let month = 1; month <= totalMonths; month++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = monthlyPayment - interestPayment;
      balance = Math.max(0, balance - principalPayment);

      monthlyData.push({
        month,
        principal: principalPayment,
        interest: interestPayment,
        balance,
      });
    }

    return monthlyData;
  };

  const monthlySchedule = generateMonthlySchedule();
  const quickYears = [10, 15, 20, 25, 30];

  return (
    <Card className={cardStyles.shadowElevated}>
      <CardHeader>
        <CardTitle style={{ fontSize: '1.125rem' }}>Amortization Schedule</CardTitle>
      </CardHeader>
      <CardContent className={styles.content}>
        {/* Year Selector */}
        <div className={styles.yearSelector}>
          <div className={styles.yearSelectorHeader}>
            <Label className={styles.yearSelectorLabel}>Payment Period</Label>
            <span className={styles.yearSelectorValue}>{selectedYears} years</span>
          </div>

          {/* Quick Select Chips */}
          <div className={styles.quickSelectButtons}>
            {quickYears.map(year => (
              <button
                key={year}
                onClick={() => setSelectedYears(year)}
                className={buildClassName(
                  styles.quickSelectButton,
                  selectedYears === year && styles.active
                )}
              >
                {year} yrs
              </button>
            ))}
          </div>

          {/* Slider for fine-tuning */}
          <Slider
            value={[selectedYears]}
            onValueChange={([value]) => setSelectedYears(value)}
            min={5}
            max={30}
            step={1}
          />
        </div>

        {/* Summary Stats */}
        <div className={styles.summaryStats}>
          <div className={styles.summaryStat}>
            <p className={styles.summaryStatLabel}>Total Principal</p>
            <p className={styles.summaryStatValue} style={{ color: 'hsl(210, 100%, 50%)' }}>${Math.round(totalPrincipal).toLocaleString('en-CA')}</p>
          </div>
          <div className={styles.summaryStat}>
            <p className={styles.summaryStatLabel}>Total Interest</p>
            <p className={styles.summaryStatValue} style={{ color: 'hsl(25, 95%, 53%)' }}>${Math.round(totalInterest).toLocaleString('en-CA')}</p>
          </div>
          <div className={styles.summaryStat}>
            <p className={styles.summaryStatLabel}>Total Payments</p>
            <p className={styles.summaryStatValue}>${Math.round(totalPayments).toLocaleString('en-CA')}</p>
          </div>
        </div>

        {/* Chart placeholder - can be enhanced with Victory charts later */}
        <div className={styles.chartPlaceholder}>
          <p className={styles.chartPlaceholderText}>Chart visualization (can be enhanced with Victory charts)</p>
        </div>

        {/* Breakdown Table */}
        <div className={styles.collapsibleWrapper}>
          <Collapsible open={isTableOpen} onOpenChange={setIsTableOpen}>
            <CollapsibleTrigger className={styles.collapsibleTrigger}>
              <span className={styles.collapsibleTriggerText}>Payment Breakdown</span>
              <span className={buildClassName(styles.collapsibleTriggerIcon, isTableOpen && styles.open)}>▼</span>
            </CollapsibleTrigger>
            <CollapsibleContent className={styles.collapsibleContent}>
              {/* View Toggle */}
              <div className={styles.viewToggle}>
                <button
                  onClick={() => setViewMode('yearly')}
                  className={buildClassName(
                    styles.viewToggleButton,
                    viewMode === 'yearly' && styles.active
                  )}
                >
                  Yearly
                </button>
                <button
                  onClick={() => setViewMode('monthly')}
                  className={buildClassName(
                    styles.viewToggleButton,
                    viewMode === 'monthly' && styles.active
                  )}
                >
                  Monthly
                </button>
              </div>

              <div className={styles.tableContainer}>
                <Table>
                  <TableHeader className={styles.tableHeaderSticky}>
                    <TableRow>
                      <TableHead style={{ width: '4rem' }}>{viewMode === 'yearly' ? 'Year' : 'Month'}</TableHead>
                      <TableHead className={styles.tableCellRight}>Principal Paid</TableHead>
                      <TableHead className={styles.tableCellRight}>Interest Paid</TableHead>
                      <TableHead className={styles.tableCellRight}>Total Payment</TableHead>
                      <TableHead className={styles.tableCellRight}>Remaining Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewMode === 'yearly' ? (
                      schedule.map(item => (
                        <TableRow key={item.year}>
                          <TableCell style={{ fontWeight: 500 }}>{item.year}</TableCell>
                          <TableCell className={styles.tableCellRight} style={{ color: 'hsl(210, 100%, 50%)' }}>
                            ${Math.round(item.principal).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight} style={{ color: 'hsl(25, 95%, 53%)' }}>
                            ${Math.round(item.interest).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight}>
                            ${Math.round(item.principal + item.interest).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight}>
                            ${Math.round(item.balance).toLocaleString('en-CA')}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      monthlySchedule.slice(0, 60).map(item => (
                        <TableRow key={item.month}>
                          <TableCell style={{ fontWeight: 500 }}>{item.month}</TableCell>
                          <TableCell className={styles.tableCellRight} style={{ color: 'hsl(210, 100%, 50%)' }}>
                            ${Math.round(item.principal).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight} style={{ color: 'hsl(25, 95%, 53%)' }}>
                            ${Math.round(item.interest).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight}>
                            ${Math.round(item.principal + item.interest).toLocaleString('en-CA')}
                          </TableCell>
                          <TableCell className={styles.tableCellRight}>
                            ${Math.round(item.balance).toLocaleString('en-CA')}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                  <TableFooter className={styles.tableFooterSticky}>
                    <TableRow>
                      <TableCell className={styles.tableCellBold}>Total</TableCell>
                      <TableCell className={buildClassName(styles.tableCellRight, styles.tableCellBold)} style={{ color: 'hsl(210, 100%, 50%)' }}>
                        ${Math.round(totalPrincipal).toLocaleString('en-CA')}
                      </TableCell>
                      <TableCell className={buildClassName(styles.tableCellRight, styles.tableCellBold)} style={{ color: 'hsl(25, 95%, 53%)' }}>
                        ${Math.round(totalInterest).toLocaleString('en-CA')}
                      </TableCell>
                      <TableCell className={buildClassName(styles.tableCellRight, styles.tableCellBold)}>
                        ${Math.round(totalPayments).toLocaleString('en-CA')}
                      </TableCell>
                      <TableCell className={buildClassName(styles.tableCellRight, styles.tableCellBold)}>$0</TableCell>
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            </CollapsibleContent>
          </Collapsible>
        </div>
      </CardContent>
    </Card>
  );
};

export default AmortizationChart;
